import React, { useState } from 'react';

export interface WebGPUBuild {
  version: string;
  uploaded_by_id: string;
  uploaded_by_username: string;
  uploaded_date: Date;
  notes: string;
}

export interface NewWebGPUBuildUploadData {
  version: string;
  file: File;
  note: string;
  setActive: boolean;
}

interface WebGPUBuildsProps {
  activeVersion: string;
  current_user_id: string;
  current_user_username: string;
  builds: WebGPUBuild[];
  onSetActive: (info: WebGPUBuild) => void;
  onDelete: (info: WebGPUBuild) => void;
  onCreate: (info: WebGPUBuild, file: File) => void;
}

const WebGPUBuilds: React.FC<WebGPUBuildsProps> = ({
  activeVersion,
  builds: initialBuilds,
  current_user_id,
  current_user_username,
  onSetActive,
  onDelete,
  onCreate
}) => {
  const [builds, setBuilds] = useState<WebGPUBuild[]>(initialBuilds);
  const [activeBuild, setActiveBuild] = useState(activeVersion);
  const [formFile, setFormFile] = useState<File | null>(null);
  const [formVersion, setFormVersion] = useState('');
  const [formNotes, setFormNotes] = useState('');
  const [formSetActive, setFormSetActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState('');

  const handleSetActive = async (build: WebGPUBuild) => {
    setActiveBuild(build.version);
    await onSetActive(build);
  };

  const handleDelete = async (build: WebGPUBuild) => {
    setBuilds((prevBuilds) => prevBuilds.filter((b) => b.version !== build.version));
    await onDelete(build);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setMessage('');
    if (!formVersion || !formFile) {
      setMessage('❌ Please enter a version and select a file.');
      return;
    }

    if (builds.some((b) => b.version === formVersion)) {
      setMessage('❌ A build with this version already exists.');
      return;
    }

    setUploading(true);
    const newWebGPUBuild: WebGPUBuild = {
      version: formVersion,
      uploaded_by_id: current_user_id,
      uploaded_by_username: current_user_username,
      uploaded_date: new Date(),
      notes: formNotes
    };

    if (formSetActive) {
      handleSetActive(newWebGPUBuild);
    }

    try {
      await onCreate(newWebGPUBuild, formFile);
      setBuilds((prevBuilds) => [...prevBuilds, newWebGPUBuild]);
      setMessage('✅ Build uploaded successfully!');
    } catch (error) {
      setMessage(`❌ Upload failed: ${(error as Error).message}`);
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFormFile(event.target.files[0]);
    }
  };

  return (
    <div className="p-6 bg-[#1f1f1f] border-white border rounded-lg shadow-md mx-auto max-w-6xl">
      <h2 className="text-white text-2xl font-bold mb-4 text-center">WebGPU Management</h2>

      {/* Table Container */}
      <div className="bg-[#1f1f1f] border-white border p-4 rounded-lg shadow mb-6 overflow-x-auto">
        <h3 className="text-white text-xl font-semibold mb-4 text-center">
          Active Version: {activeBuild}
        </h3>
        <table className="table-auto w-full text-white border-collapse">
          <thead>
            <tr className="border-b border-white text-left">
              <th className="px-2 py-2 w-fit min-w-[100px]">Version</th>
              <th className="px-2 py-2 w-fit min-w-[120px]">Uploaded By</th>
              <th className="px-2 py-2 w-fit min-w-[120px] whitespace-nowrap">Uploaded On</th>
              <th className="px-2 py-2 w-fit min-w-[150px] truncate">Note</th>
              <th className="px-2 py-2 w-fit min-w-[120px]">Actions</th>
            </tr>
          </thead>
          <tbody>
            {builds.map((build) => (
              <tr className="border-b border-gray-600" key={build.version}>
                <td className="px-2 py-2 min-w-0 truncate">{build.version}</td>
                <td className="px-2 py-2 min-w-0 truncate">{build.uploaded_by_username}</td>
                <td className="px-2 py-2 whitespace-nowrap">
                  {new Date(build.uploaded_date).toLocaleDateString()}
                </td>
                <td className="px-2 py-2 min-w-0 break-words whitespace-normal">{build.notes}</td>
                <td className="px-2 py-2 space-x-2 flex whitespace-nowrap">
                  <button
                    disabled={build.version === activeBuild}
                    onClick={() => handleSetActive(build)}
                    className={`px-4 py-2 rounded text-white whitespace-nowrap ${
                      build.version === activeBuild
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-[#5239ea] hover:bg-[#3a29c8]'
                    }`}
                  >
                    Set Active
                  </button>
                  <button
                    disabled={build.version === activeBuild}
                    onClick={() => handleDelete(build)}
                    className={`px-4 py-2 rounded text-white ${
                      build.version === activeBuild
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-[#5239ea] hover:bg-[#3a29c8]'
                    }`}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Upload Form */}
      <div className="bg-[#1f1f1f] border-white border p-6 rounded-lg shadow-md max-w-lg mx-auto">
        <h2 className="text-xl font-bold text-white mb-4 text-center">Upload WebGPU Build</h2>
        {message && <p className="text-sm text-white mt-3 mb-3">{message}</p>}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="text-white block text-sm font-medium mb-1" htmlFor="version">
              Version
            </label>
            <input
              id="version"
              type="text"
              value={formVersion}
              onChange={(e) => setFormVersion(e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-gray-700 text-white border border-gray-600 focus:ring focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="text-white block text-sm font-medium mb-1" htmlFor="file">
              Select File
            </label>
            <input
              id="file"
              type="file"
              accept=".zip"
              onChange={handleFileChange}
              className="w-full px-3 py-2 rounded-md bg-gray-700 text-white border border-gray-600 cursor-pointer"
              required
            />
          </div>

          <div>
            <label className="text-white block text-sm font-medium mb-1" htmlFor="notes">
              Notes
            </label>
            <textarea
              id="notes"
              value={formNotes}
              onChange={(e) => setFormNotes(e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-gray-700 text-white border border-gray-600 focus:ring focus:ring-blue-500"
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              id="set-active"
              type="checkbox"
              checked={formSetActive}
              onChange={(e) => setFormSetActive(e.target.checked)}
              className="h-4 w-4"
            />
            <label className="text-white text-sm" htmlFor="set-active">
              Set this build as active
            </label>
          </div>

          <button
            type="submit"
            disabled={uploading}
            className="w-full bg-[#5239ea] hover:bg-[#3a29c8] text-white px-4 py-2 rounded-md"
          >
            {uploading ? 'Uploading...' : 'Upload Build'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default WebGPUBuilds;
